/**
 * smart-set-builder.js v3
 * Simple, reliable set builder. One product per category, no fallback chains.
 * Test via console: window._testSmartSet({ stage: 't1', size: 'M', country: 'AU' })
 */
(function(){
  'use strict';

  // ── Core Products (one per category) ──
  var PRODUCTS = {
    matLeggings:   {handle:'7-8-mat-pocket-leggings-black',              name:'7/8 Maternity Leggings + Pockets'},
    matShorts:     {handle:'shorts-mat-pocket-black',                     name:'Maternity Shorts + Pockets'},
    recLeggings:   {handle:'pregnancy-recovery-emama-7-8-leggings-pockets', name:'Recovery 7/8 Leggings + Pockets'},
    recShorts:     {handle:'pregnancy-recovery-emama-shorts-pockets',     name:'Recovery Shorts + Pockets'},
    nursingCrop:   {handle:'emama-longline-crop',                         name:'Long Line Nursing Crop'},
    shaperLeggings:{handle:'7-8-shaper-move-leggings-pockets-black',     name:'7/8 Shaper Move + Pockets'},
    shaperShorts:  {handle:'shorts-shaper-move-pockets-black',            name:'Shaper Move Shorts + Pockets'},
    shapewearLeggings:{handle:'body-shapewear-leggings-pockets-full-length-black', name:'Body Shapewear 7/8 Leggings + Pockets'},
    shapewearShorts:  {handle:'body-shapewear-shorts-pockets',            name:'Body Shapewear Shorts + Pockets'},
  };

  // ── Tops pool (try these, fallback to shaperShorts) ──
  var TOPS = [
    {handle:'racer-back-lilac',           name:'Racer Back - Lilac'},
    {handle:'racer-back-powder-blue',     name:'Racer Back - Powder Blue'},
    {handle:'singlet-top-pink',           name:'Singlet Top - Pink'},
    {handle:'racer-back-olive',           name:'Racer Back - Olive'},
    {handle:'singlet-top-rose',           name:'Singlet Top - Rose'},
    {handle:'tank-top-white',             name:'Tank Top - Black'},
    {handle:'tank-top-white-1',           name:'Tank Top - White'},
    {handle:'white-high-neck-jumper',     name:'Snow High Neck Jumper'},
    {handle:'charcoal-high-neck-jumper-1',name:'Charcoal Jumper'},
    {handle:'crop-jumper',                name:'Cropped Sweat'},
    {handle:'tshirt-white',               name:'Tshirt - Rose'},
    {handle:'tshirt-twilight',            name:'Tshirt - Twilight'},
    {handle:'tshirt-black',               name:'Tshirt - Black'},
    {handle:'basic-tee-lavender',         name:'Basic Tee - Lavender'},
    {handle:'basic-tee-violet',           name:'Basic Tee - Violet'},
    {handle:'light-blue-tee',             name:'Light Blue Tee'},
  ];

  // ── Set Templates ──
  var SET_TEMPLATES = {
    t1: {
      essentials: ['matLeggings:3','matShorts:1','nursingCrop:1'],
      prepared:   ['matLeggings:3','matShorts:2','nursingCrop:2'],
      ultimate:   ['matLeggings:4','matShorts:3','nursingCrop:2'],
    },
    t2: {
      essentials: ['matLeggings:1','matShorts:1','nursingCrop:1','recLeggings:1','recShorts:1'],
      prepared:   ['matLeggings:1','matShorts:2','nursingCrop:2','recLeggings:1','recShorts:1'],
      ultimate:   ['matLeggings:2','matShorts:2','nursingCrop:2','recLeggings:1','recShorts:2'],
    },
    t3: {
      essentials: ['matLeggings:1','matShorts:1','nursingCrop:1','recLeggings:1','recShorts:1'],
      prepared:   ['matLeggings:1','matShorts:1','nursingCrop:2','recLeggings:2','recShorts:1'],
      ultimate:   ['matLeggings:1','matShorts:1','nursingCrop:2','recLeggings:2','recShorts:3'],
    },
    postpartum: {
      essentials: ['recLeggings:2','recShorts:1','nursingCrop:2'],
      prepared:   ['recLeggings:2','recShorts:2','nursingCrop:2','shaperShorts:1'],
      ultimate:   ['recLeggings:2','recShorts:2','nursingCrop:2','shaperShorts:2','shaperLeggings:1'],
    },
    mumlife: {
      essentials: ['shaperLeggings:1','shaperShorts:1','top:1','shapewearShorts:1','shapewearLeggings:1'],
      prepared:   ['shaperLeggings:2','shaperShorts:2','top:1','shapewearShorts:1','shapewearLeggings:1'],
      ultimate:   ['shaperLeggings:2','shaperShorts:2','top:2','shapewearShorts:2','shapewearLeggings:1'],
    },
  };

  // ── Size-up for recovery ──
  var SIZE_LADDER = ['XXS','XS','S','M','L','XL','XXL','XXXL'];
  function sizeUp(size) {
    var idx = SIZE_LADDER.indexOf(size);
    if (idx < 0 || idx >= SIZE_LADDER.length - 1) return size;
    return SIZE_LADDER[idx + 1];
  }

  // ── Stage mapping ──
  function stageToGroup(stage) {
    if (stage === 't1') return 't1';
    if (stage === 't2') return 't2';
    if (stage === 't3') return 't3';
    if (stage === 'pp') return 'postpartum';
    return 'mumlife';
  }

  // ── Stock cache ──
  var _stockCache = {};

  // ── Available-handles cache ──
  // Smart-set's hardcoded PRODUCTS + TOPS lists include items that are
  // published on some storefronts but not others (Shopify Markets:
  // emamaco.com publishes a smaller subset than emamaco.com.au).
  // Without filtering, every per-handle fetch on a storefront where
  // that product isn't published 404s — noisy in DevTools and slow.
  // Resolve once against /collections/all/products.json and use that
  // set to short-circuit unavailable-handle fetches.
  var _availablePromise = null;
  function getAvailableHandles() {
    if (_availablePromise) return _availablePromise;
    if (window._v2AvailableHandles && typeof window._v2AvailableHandles.has === 'function') {
      _availablePromise = Promise.resolve(window._v2AvailableHandles);
      return _availablePromise;
    }
    _availablePromise = fetch('/collections/all/products.json?limit=250')
      .then(function(r) { return r.ok ? r.json() : { products: [] }; })
      .then(function(d) {
        var set = new Set();
        (d.products || []).forEach(function(p) { if (p && p.handle) set.add(p.handle); });
        window._v2AvailableHandles = set;
        return set;
      })
      .catch(function() {
        var empty = new Set();
        window._v2AvailableHandles = empty;
        return empty;
      });
    return _availablePromise;
  }

  function fetchStock(handle) {
    if (_stockCache[handle]) return _stockCache[handle];
    _stockCache[handle] = getAvailableHandles().then(function(available) {
      // Storefront doesn't publish this handle — skip the fetch and the
      // 404 it would cause, return an empty record so callers can treat
      // the product as unavailable.
      if (available.size > 0 && !available.has(handle)) {
        return { handle: handle, title: handle, variants: {}, image: '', _unavailable: true };
      }
      return fetch('/products/' + handle + '.js')
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(product) {
          if (!product) return { handle: handle, title: handle, variants: {}, image: '' };
          var variants = {};
          (product.variants || []).forEach(function(v) {
            var size = (v.title || '').trim().toUpperCase();
            variants[size] = { id: v.id, available: v.available, price: v.price, compareAtPrice: v.compare_at_price };
          });
          return { handle: handle, title: product.title, variants: variants, image: product.featured_image };
        })
        .catch(function() {
          return { handle: handle, title: handle, variants: {}, image: '' };
        });
    });
    return _stockCache[handle];
  }

  // ── Pre-load ──
  function preloadAllStock() {
    getAvailableHandles().then(function(available) {
      Object.keys(PRODUCTS).forEach(function(cat) {
        var h = PRODUCTS[cat].handle;
        if (available.size === 0 || available.has(h)) fetchStock(h);
      });
      TOPS.forEach(function(t) {
        if (available.size === 0 || available.has(t.handle)) fetchStock(t.handle);
      });
    });
  }

  // Sequential preload — one fetch at a time, no connection flooding
  function preloadSequential() {
    getAvailableHandles().then(function(available) {
      var handles = [];
      Object.keys(PRODUCTS).forEach(function(cat) {
        var h = PRODUCTS[cat].handle;
        if (available.size > 0 && !available.has(h)) return;
        if (handles.indexOf(h) < 0) handles.push(h);
      });
      TOPS.forEach(function(t) {
        if (available.size > 0 && !available.has(t.handle)) return;
        if (handles.indexOf(t.handle) < 0) handles.push(t.handle);
      });
      var i = 0;
      function next() {
        if (i >= handles.length) return;
        fetchStock(handles[i++]).then(function(){ setTimeout(next, 50); });
      }
      next();
    });
  }

  // ── Find a top that's available ──
  // Tops don't carry XXS or XXXL — map to nearest available size
  var TOP_SIZE_MAP = { 'XXS': 'XS', 'XXXL': 'XXL' };
  function pickTop(size) {
    var topSize = TOP_SIZE_MAP[size] || size;
    var fetches = TOPS.map(function(t) { return fetchStock(t.handle); });
    return Promise.all(fetches).then(function(results) {
      for (var i = 0; i < results.length; i++) {
        var v = results[i].variants[topSize];
        if (v && v.available) {
          return { handle: TOPS[i].handle, name: TOPS[i].name, variantId: v.id, pickedSize: topSize, reason: 'top', image: results[i].image };
        }
      }
      // Fallback: shaper shorts in user's original size
      return fetchStock(PRODUCTS.shaperShorts.handle).then(function(stock) {
        var v = stock.variants[size] || stock.variants[topSize];
        var usedSize = stock.variants[size] ? size : topSize;
        if (v) return { handle: PRODUCTS.shaperShorts.handle, name: PRODUCTS.shaperShorts.name, variantId: v.id, pickedSize: usedSize, reason: 'top-fallback', image: stock.image };
        return null;
      });
    });
  }

  // ── Main builder ──
  function buildSmartSet(opts) {
    var stage = opts.stage;
    var size = (opts.size || 'M').toUpperCase();
    var group = stageToGroup(stage);
    var templates = SET_TEMPLATES[group];
    if (!templates) return Promise.reject(new Error('Unknown stage: ' + stage));

    var recSize = sizeUp(size);
    var result = {};
    var tierNames = ['essentials', 'prepared', 'ultimate'];

    function buildTier(tierIdx) {
      if (tierIdx >= tierNames.length) return Promise.resolve(result);
      var tierName = tierNames[tierIdx];
      var template = templates[tierName];
      var items = [];
      var topCount = 0;

      function buildSlot(slotIdx) {
        if (slotIdx >= template.length) {
          result[tierName] = { items: items };
          return buildTier(tierIdx + 1);
        }

        var parts = template[slotIdx].split(':');
        var cat = parts[0];
        var qty = parseInt(parts[1], 10);

        // Top slot
        if (cat === 'top') {
          topCount++;
          return pickTop(size).then(function(picked) {
            if (picked) {
              // Check if we already have this top
              var existing = null;
              for (var e = 0; e < items.length; e++) {
                if (items[e].handle === picked.handle) { existing = items[e]; break; }
              }
              if (existing) {
                existing.qty += qty;
              } else {
                items.push({ handle: picked.handle, name: picked.name, qty: qty, reason: picked.reason, variantId: picked.variantId, pickedSize: picked.pickedSize, image: picked.image });
              }
            }
            return buildSlot(slotIdx + 1);
          });
        }

        // Regular product
        var product = PRODUCTS[cat];
        if (!product) return buildSlot(slotIdx + 1);

        // Recovery products get sized up. Nursing crop maps XXS→XS, XXXL→XXL (no XXS/XXXL variants)
        var useSize = (cat === 'recLeggings' || cat === 'recShorts') ? recSize : size;
        if (cat === 'nursingCrop') { var _cropMap = {'XXS':'XS','XXXL':'XXL'}; if (_cropMap[useSize]) useSize = _cropMap[useSize]; }

        return fetchStock(product.handle).then(function(stock) {
          var v = stock.variants[useSize];
          // If sized-up not available, try original size
          if ((!v || !v.available) && useSize !== size) {
            v = stock.variants[size];
            useSize = size;
          }
          // If still not available, use the target size variant anyway (preorder/continue-selling)
          if (!v || !v.available) {
            var targetSize = (cat === 'recLeggings' || cat === 'recShorts') ? recSize : size;
            v = stock.variants[targetSize] || stock.variants[size];
            useSize = targetSize;
          }
          if (v) {
            // Check if we already have this product
            var existing = null;
            for (var e = 0; e < items.length; e++) {
              if (items[e].handle === product.handle) { existing = items[e]; break; }
            }
            if (existing) {
              existing.qty += qty;
            } else {
              items.push({ handle: product.handle, name: product.name, qty: qty, reason: 'core', variantId: v.id, pickedSize: useSize, image: stock.image });
            }
          }
          return buildSlot(slotIdx + 1);
        });
      }

      return buildSlot(0);
    }

    return buildTier(0).then(function() {
      return { stage: stage, group: group, size: size, sets: result };
    });
  }

  // ── Test Interface ──
  function runTest(opts) {
    _stockCache = {};
    console.log('\n%c[SmartSet] ' + opts.stage.toUpperCase() + ' / ' + opts.size + ' / ' + (opts.country || 'AU'), 'font-weight:bold;color:#E8846C;font-size:13px');
    return buildSmartSet(opts).then(function(result) {
      ['essentials', 'prepared', 'ultimate'].forEach(function(tier) {
        if (!result.sets[tier]) return;
        var total = 0;
        var lines = [];
        result.sets[tier].items.forEach(function(item) {
          total += item.qty;
          lines.push('  ' + item.qty + 'x ' + item.name + ' [' + (item.pickedSize || '?') + '] ' + (item.variantId ? 'id=' + item.variantId : 'NO VARIANT'));
        });
        console.log('  %c' + tier.toUpperCase() + ' (' + total + ' items):', 'font-weight:bold');
        lines.forEach(function(l) { console.log(l); });
      });
      return result;
    });
  }

  // ── Expose ──
  window._smartSetBuilder = { build: buildSmartSet, preload: preloadAllStock, preloadSequential: preloadSequential, PRODUCTS: PRODUCTS, TOPS: TOPS };
  window._testSmartSet = runTest;

  // Auto-preload all product stock on script load — makes first set click instant
  preloadAllStock();
})();

